import ENV from "@/env"
import { NATIVE_TOKENS } from "@/settings"
import { Tag } from "@/types"
import { dryrun } from "./ao-connection"
import { useQuery } from "@tanstack/react-query"
import { getMessageById } from "./messages-api"
import { getTotalSupply } from "@/hooks/useTotalSupply"
import { getHBBaseUrl } from "@/utils/hb-router"

type TokenDetailsR = {
  TokenProcess: string
  InternalId: string
  Deployer: string
  Name: string
  Ticker: string
  Denomination: number
  Description: string
  Balances: Record<string, number>
  TotalSupply: number
  Telegram: string
  Twitter: string
  Website: string
  Logo: string
  Status: string
  LPs: Record<string, string>
  RenounceOwnership: string | boolean
}

export type TokenDetails = {
  TokenProcess: string
  InternalId: string
  Deployer: string
  Name: string
  Ticker: string
  Denomination: number
  Description: string
  Balances: Record<string, number>
  TotalSupply: number
  Logo: string
  Status: string
  LPs: Record<string, string>
  RenounceOwnership: boolean
  SocialLinks?: Record<string, string> | { key: string; value: string }[]
  CoverImage?: string
}

async function getTokenDetailsHB(tokenProcess: string): Promise<TokenDetails> {
  console.log("GET TOKEN DETAILS HB", tokenProcess)
  const response = await fetch(
    `${ENV.VITE_HB_API}/${ENV.VITE_TOKENDROP_CACHE_PROCESS}~process@1.0/compute/tokens/${tokenProcess}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching token details: ${response.statusText}`)
  }

  const data = (await response.json()) as TokenDetailsR

  if (!data) throw new Error("Response malformed")

  return {
    ...data,
    RenounceOwnership:
      typeof data.RenounceOwnership === "string"
        ? data.RenounceOwnership === "true"
        : data.RenounceOwnership,
  }
}

async function getTokenDetailsDryrun(tokenProcess: string): Promise<TokenDetails> {
  // Get token info
  const details = await getTokenInfoHB(tokenProcess)

  return {
    TokenProcess: tokenProcess,
    InternalId: "external",
    Deployer: "unknown",
    Name: details.name ?? "",
    Ticker: details.ticker ?? "",
    Denomination: details.denomination ? Number(details.denomination) : 0,
    Description: "",
    Balances: {},
    TotalSupply: details.supply ? Number(details.supply) : 0,
    Logo: details.logo ?? "",
    Status: "EXTERNAL",
    LPs: {},
    RenounceOwnership: false,
  }
}

async function getTokenInfoHB(tokenProcess: string): Promise<{
  denomination: string
  logo: string
  name: string
  supply: string
  ticker: string
}> {
  console.log("GET TOKEN INFO HB", tokenProcess)

  const HB_API = getHBBaseUrl(tokenProcess)

  const response = await fetch(
    `${HB_API}/${tokenProcess}~process@1.0/compute/token-info?require-codec=application/json&accept-bundle=true`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching token details: ${response.statusText}`)
  }

  const data = (await response.json()) as {
    body: {
      denomination: string
      logo: string
      name: string
      supply: string
      ticker: string
    }
  }

  if (!data) throw new Error("Response malformed")

  return {
    ...data.body,
  }
}

export async function getTokenDetails(tokenProcess: string): Promise<TokenDetails> {
  try {
    const isNative = NATIVE_TOKENS.find((token) => token.TokenProcess === tokenProcess)

    if (isNative) {
      // Get token Total-Supply
      const result2 = await getTotalSupply(tokenProcess)

      return {
        ...isNative,
        InternalId: "external",
        TotalSupply: result2 ? Number(result2) : 0,
      }
    }

    // Try to get token details from HB
    const details = getTokenDetailsHB(tokenProcess)

    return details
  } catch (error) {
    // If the error is related to HB, fallback to the dryrun method
    const tokenDetails = getTokenDetailsDryrun(tokenProcess)

    if (!tokenDetails) {
      throw new Error(`Error fetching token details: ${error}`)
    }

    return tokenDetails
  }
}
